import { getEntityMatchScore } from 'utils/nameMatchScore';
import { parseEntityLensNames } from 'utils/entityLensBadge';

export type EntityHitSortKey =
  | 'name'
  | 'entityCountry'
  | 'sourceCountry'
  | 'agency'
  | 'lens'
  | 'list'
  | 'matchScore'
  | 'dateAdded';

export type SortDirection = 'asc' | 'desc';

export interface EntityHitFilters {
  lenses: string[];
  listNames: string[];
  entityCountries: string[];
  matchScoreMin: number | null;
  matchScoreMax: number | null;
}

export const EMPTY_ENTITY_HIT_FILTERS: EntityHitFilters = {
  lenses: [],
  listNames: [],
  entityCountries: [],
  matchScoreMin: null,
  matchScoreMax: null,
};

const asText = (raw: unknown): string => String(raw || '').trim();
export const parseMatchScoreFilter = (value: string): number | null => {
  if (value.trim() === '') {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return Math.min(100, Math.max(0, Math.round(parsed)));
};

export const getHitName = (hit: Record<string, unknown>): string =>
  asText(hit['Name of Entity'] || hit['Product Name']);

export const getHitEntityCountry = (hit: Record<string, unknown>): string =>
  asText(hit['Entity Country'] || hit['Entity Country iso-2 Code']);

export const getHitSourceCountry = (hit: Record<string, unknown>): string =>
  asText(hit['Source Country']);

export const getHitAgency = (hit: Record<string, unknown>): string =>
  asText(hit.Agency);

export const getHitListName = (hit: Record<string, unknown>): string =>
  asText(hit['List Name']);

export const getHitLensLabel = (hit: Record<string, unknown>): string =>
  parseEntityLensNames(hit).join(', ');

export const getHitDate = (hit: Record<string, unknown>): string =>
  asText(hit['Date Updated on BITE Platform']);

export const getHitMatchScore = (
  hit: Record<string, unknown>,
  matchTerm: string,
): number | null => getEntityMatchScore(matchTerm, hit);

const uniqueSorted = (values: string[]): string[] =>
  Array.from(new Set(values.filter(Boolean))).sort((left, right) =>
    left.localeCompare(right),
  );

export const collectHitFilterOptions = (
  hits: Record<string, unknown>[],
): {
  lenses: string[];
  listNames: string[];
  entityCountries: string[];
} => ({
  lenses: uniqueSorted(hits.flatMap((hit) => parseEntityLensNames(hit))),
  listNames: uniqueSorted(hits.map((hit) => getHitListName(hit))),
  entityCountries: uniqueSorted(hits.map((hit) => getHitEntityCountry(hit))),
});

export const hasActiveHitFilters = (filters: EntityHitFilters): boolean =>
  filters.lenses.length > 0 ||
  filters.listNames.length > 0 ||
  filters.entityCountries.length > 0 ||
  filters.matchScoreMin !== null ||
  filters.matchScoreMax !== null;

const matchesSearch = (
  hit: Record<string, unknown>,
  searchQuery: string,
): boolean => {
  if (!searchQuery) {
    return true;
  }
  const needle = searchQuery.toLowerCase();
  return Object.values(hit).some(
    (value) =>
      typeof value === 'string' && value.toLowerCase().includes(needle),
  );
};

export const filterEntityHits = ({
  hits,
  filters,
  searchQuery,
  matchTerm,
}: {
  hits: Record<string, unknown>[];
  filters: EntityHitFilters;
  searchQuery: string;
  matchTerm: string;
}): Record<string, unknown>[] =>
  hits.filter((hit) => {
    if (!matchesSearch(hit, searchQuery)) {
      return false;
    }
    if (filters.lenses.length > 0) {
      const hitLenses = parseEntityLensNames(hit);
      if (!hitLenses.some((lens) => filters.lenses.includes(lens))) {
        return false;
      }
    }
    if (
      filters.listNames.length > 0 &&
      !filters.listNames.includes(getHitListName(hit))
    ) {
      return false;
    }
    if (
      filters.entityCountries.length > 0 &&
      !filters.entityCountries.includes(getHitEntityCountry(hit))
    ) {
      return false;
    }
    const score = getHitMatchScore(hit, matchTerm);
    if (filters.matchScoreMin !== null) {
      if (score === null || score < filters.matchScoreMin) {
        return false;
      }
    }
    if (filters.matchScoreMax !== null) {
      if (score === null || score > filters.matchScoreMax) {
        return false;
      }
    }
    return true;
  });

const compareText = (left: string, right: string): number =>
  left.localeCompare(right, undefined, { sensitivity: 'base' });

const compareNullableNumber = (
  left: number | null,
  right: number | null,
): number => {
  if (left === null && right === null) {
    return 0;
  }
  if (left === null) {
    return 1;
  }
  if (right === null) {
    return -1;
  }
  return left - right;
};

const valueForSort = (
  hit: Record<string, unknown>,
  key: EntityHitSortKey,
  matchTerm: string,
): string | number | null => {
  switch (key) {
    case 'name':
      return getHitName(hit);
    case 'entityCountry':
      return getHitEntityCountry(hit);
    case 'sourceCountry':
      return getHitSourceCountry(hit);
    case 'agency':
      return getHitAgency(hit);
    case 'lens':
      return getHitLensLabel(hit);
    case 'list':
      return getHitListName(hit);
    case 'matchScore':
      return getHitMatchScore(hit, matchTerm);
    case 'dateAdded':
      return getHitDate(hit);
    default: {
      const _exhaustive: never = key;
      return _exhaustive;
    }
  }
};

export const sortEntityHits = ({
  hits,
  sortKey,
  sortDirection,
  matchTerm,
}: {
  hits: Record<string, unknown>[];
  sortKey: EntityHitSortKey;
  sortDirection: SortDirection;
  matchTerm: string;
}): Record<string, unknown>[] => {
  const direction = sortDirection === 'asc' ? 1 : -1;
  return [...hits].sort((left, right) => {
    const leftValue = valueForSort(left, sortKey, matchTerm);
    const rightValue = valueForSort(right, sortKey, matchTerm);
    if (typeof leftValue === 'number' || typeof rightValue === 'number') {
      return (
        compareNullableNumber(
          typeof leftValue === 'number' ? leftValue : null,
          typeof rightValue === 'number' ? rightValue : null,
        ) * direction
      );
    }
    return compareText(String(leftValue), String(rightValue)) * direction;
  });
};

export const getEntityHitDocs = (
  passedDownResults: Array<{ field?: string; docs?: Record<string, unknown>[] }> | undefined,
  entityName: string,
): Record<string, unknown>[] => {
  if (!passedDownResults?.length) {
    return [];
  }
  const matchName = entityName.toLowerCase().trim();
  const matched = passedDownResults.find(
    (item) => String(item.field || '').toLowerCase().trim() === matchName,
  );
  // Fall back to the sole entry only when there is exactly one -- the legacy
  // single-result shape, where `field` may be absent and the choice is
  // unambiguous. With several entries and no name match, guessing the first
  // would show/hide/download a different entity's screening hits.
  const docs =
    matched?.docs ??
    (passedDownResults.length === 1 ? passedDownResults[0]?.docs : undefined);
  return Array.isArray(docs) ? docs : [];
};

export const toStaticScreeningResults = (
  docs: Record<string, unknown>[],
  isLoading = false,
  hitTotal?: number,
): {
  isLoading: boolean;
  data: Array<{
    result: {
      body: {
        hits: {
          total: { value: number };
          hits: Array<{ _id: string; _source: Record<string, unknown> }>;
        };
      };
    };
  }>;
} => ({
  isLoading: isLoading,
  data: [
    {
      result: {
        body: {
          hits: {
            total: { value: hitTotal ?? docs.length },
            hits: docs.map((doc, index) => ({
              _id: String(doc['BITE Record ID'] ?? doc.id ?? index),
              _source: doc,
            })),
          },
        },
      },
    },
  ],
});
